/**
 * Activity feed (GET /activity, migration 0112) — the bell in the top bar and the /app/activity page.
 * Every event is derived server-side from the workflow's own records, so this file only renders:
 * an icon per kind, the sentence, the requirement, the time, and read / unread.
 */
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, Bot, Briefcase, CalendarCheck, FileSearch, FileText, Mail, Send, UserCheck, UserPlus } from "lucide-react";
import { api } from "../../lib/api.js";

export type ActivityCategory = "applications" | "resumes" | "interviews" | "requirements" | "team";

export interface ActivityEvent {
  key: string;
  at: string;
  category: ActivityCategory;
  kind: string;
  text: string;
  actor: string | null;
  subject: string | null;
  requisition: { id: string; title: string | null } | null;
  candidacyId: string | null;
  read: boolean;
}

export interface ActivityPage {
  events: ActivityEvent[];
  unread: number;
  nextBefore: string | null;
}

export const ACTIVITY_FILTERS: { key: ActivityCategory | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "applications", label: "Applications" },
  { key: "resumes", label: "Resumes" },
  { key: "interviews", label: "Interviews" },
  { key: "requirements", label: "Requirements" },
  { key: "team", label: "System / Team" },
];

/** The bell's read: the latest few events + the unread count, refreshed every minute. */
export function useActivityPreview(limit = 6) {
  return useQuery({
    queryKey: ["activity", "preview", limit],
    queryFn: () => api.get<ActivityPage>(`/activity?limit=${limit}`),
    refetchInterval: 60_000,
    staleTime: 20_000,
  });
}

export function useMarkActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { all: true } | { keys: string[]; read: boolean }) => api.post("/activity/read", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["activity"] }),
  });
}

/** Where an event opens: the candidate's review page, else the requirement. */
export function activityHref(e: ActivityEvent): string | null {
  if (e.candidacyId) return `/app/review/${e.candidacyId}`;
  if (e.requisition) return `/app/requisitions/${e.requisition.id}`;
  return null;
}

const ICONS: Record<string, { icon: ReactNode; tone: string }> = {
  applied: { icon: <UserPlus />, tone: "app" },
  imported: { icon: <UserPlus />, tone: "app" },
  added: { icon: <UserPlus />, tone: "app" },
  resume_received: { icon: <FileText />, tone: "res" },
  resume_screened: { icon: <FileSearch />, tone: "res" },
  cv_requested: { icon: <Mail />, tone: "res" },
  invited: { icon: <Send />, tone: "int" },
  interview_completed: { icon: <CalendarCheck />, tone: "done" },
  evaluated: { icon: <Bot />, tone: "done" },
  requirement_created: { icon: <Briefcase />, tone: "req" },
};

export function ActivityIcon({ kind }: { kind: string }): ReactNode {
  const m = ICONS[kind] ?? (kind.startsWith("decision_") ? { icon: kind === "decision_advance" ? <Award /> : <UserCheck />, tone: "team" } : { icon: <Briefcase />, tone: "req" });
  return (
    <span className={`t1-act-ic ${m.tone}`} aria-hidden="true">
      {m.icon}
    </span>
  );
}

export function timeAgo(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** One event line — shared by the bell dropdown and the Activity page. */
export function ActivityItem({ e, onOpen, trailing }: { e: ActivityEvent; onOpen: (e: ActivityEvent) => void; trailing?: ReactNode }): ReactNode {
  return (
    <div className={`t1-act-item${e.read ? "" : " unread"}`}>
      <button type="button" className="t1-act-main" onClick={() => onOpen(e)}>
        <ActivityIcon kind={e.kind} />
        <span className="t1-act-text">
          <span className="t1-act-line">{e.text}</span>
          <span className="t1-act-meta">
            {e.requisition?.title && e.kind !== "requirement_created" ? `${e.requisition.title} · ` : ""}
            <time dateTime={e.at} title={new Date(e.at).toLocaleString()}>
              {timeAgo(e.at)}
            </time>
          </span>
        </span>
        {!e.read && <span className="t1-act-dot" aria-label="Unread" />}
      </button>
      {trailing}
    </div>
  );
}
